import { useEffect, useMemo } from "react";

import { UseInfiniteQueryResult } from "@tanstack/react-query";

import {
    ExistingControlResultPaginatedListDto as ControlResultPaginatedListDto,
    FutureControlResultPaginatedListDto,
    DashboardFilters,
    ControlProgramOverviewResourceListDto as ControlProgramOverviewResources,
    ControlProgramOverviewDtoContent,
} from "api/generated/model";
import {
    DateRange,
    formattedDateRange,
    getDateRange,
} from "components/filters/dateRange";
import { useControlProgramsSuspenseQuery } from "org/control/queries/useControlProgramsQuery";
import { useCurrentUserTimezone } from "utils/currentTimezone";

import { useExistingControlResultsQuery } from "../../controlActivities/queries/useExistingControlResultsQuery";
import { defaultPaginatedListDto } from "../components/dashboardFilters/consts";
import { useDashboardFutureControlResultsQuery } from "../queries/useDashboardFutureControlResultsQuery";
import { ExtractedTagCategory } from "../types";
import {
    extractTagCategoriesFromControlResults,
    applyTagFiltering,
    applyEntityFiltering,
    deduplicateArrayByKeyCallback,
} from "../utils/filterUtils";

interface DashboardFilteredData {
    filteredControlPrograms: ControlProgramOverviewDtoContent[];
    filteredControlResultsDto: ControlResultPaginatedListDto;
    filteredFutureControlResultsDto: FutureControlResultPaginatedListDto;
    /**
     * `null` while a `Custom` time period has not had both bounds picked yet —
     * the dashboard skips fetching in that case.
     */
    dateRange: DateRange | null;
    isLoading: boolean;
    extractedTagCategories: ExtractedTagCategory[];
}

export function useDashboardFilteredData({
    timePeriod,
    showInactivePrograms,
    showArchivedActivities,
    tags: filteredTagNames,
    controlPrograms: filteredControlProgramIds,
    entities: filteredEntityIds,
}: DashboardFilters): DashboardFilteredData {
    const timezone = useCurrentUserTimezone();
    const dateRange = useMemo(
        () => getDateRange(timePeriod, { timezone, weekStartsOn: "locale" }),
        [timePeriod, timezone]
    );
    const params = useMemo(() => {
        const dates = dateRange ? formattedDateRange(dateRange) : null;
        const baseParams = {
            limit: 10000,
            dueDateStart: dates?.startDate,
            dueDateEnd: dates?.endDate,
            controlProgramIds: filteredControlProgramIds,
            enabled: dates !== null,
        };

        // Only add archived_status: "NotArchived" if showArchivedActivities is false
        if (!showArchivedActivities) {
            return { ...baseParams, archivedStatus: "NotArchived" as const };
        }
        // If true, do not add the archived_status param (could be "All" otherwise)
        return baseParams;
    }, [dateRange, filteredControlProgramIds, showArchivedActivities]);

    const existingQuery = useExistingControlResultsQuery(params);
    const {
        data: controlResultsDto,
        hasNextPage,
        isFetching,
        isError,
        fetchNextPage,
    } = existingQuery;
    // Force the fetch of the whole dataset.
    useEffect(() => {
        if (hasNextPage && !isFetching && !isError) {
            void fetchNextPage();
        }
    }, [hasNextPage, isFetching, isError, fetchNextPage]);

    const futureQuery = useDashboardFutureControlResultsQuery(params);
    const {
        data: futureControlResultsDto,
        hasNextPage: hasFutureNextPage,
        isFetching: isFutureFetching,
        isError: isFutureError,
        fetchNextPage: fetchFutureNextPage,
    } = futureQuery;
    useEffect(() => {
        if (hasFutureNextPage && !isFutureFetching && !isFutureError) {
            void fetchFutureNextPage();
        }
    }, [
        hasFutureNextPage,
        isFutureFetching,
        isFutureError,
        fetchFutureNextPage,
    ]);

    const isComplete =
        isPaginatedQueryComplete(params.enabled, existingQuery) &&
        isPaginatedQueryComplete(params.enabled, futureQuery);

    const { data: controlProgramsDto } = useControlProgramsSuspenseQuery();

    // Apply content filtering (Active/Inactive right now, future will include archived/non-archived)
    const { existingControlResults, futureControlResults } = useMemo(() => {
        if (!controlProgramsDto || !controlResultsDto) {
            return {
                existingControlResults: [],
                futureControlResults: [],
            };
        }

        const baseDto = {
            ...controlResultsDto,
            content: controlResultsDto.content,
        };

        if (!futureControlResultsDto) {
            return {
                existingControlResults: baseDto.content,
                futureControlResults: [],
            };
        }

        const futureDto = {
            ...futureControlResultsDto,
            content: futureControlResultsDto.content,
        };

        const { filteredControlResults, filteredFutureControlResults } =
            getFilteredProgramsAndResults(
                showInactivePrograms,
                controlProgramsDto,
                baseDto,
                futureDto
            );
        return {
            existingControlResults: filteredControlResults,
            futureControlResults: filteredFutureControlResults,
        };
    }, [
        showInactivePrograms,
        controlProgramsDto,
        controlResultsDto,
        futureControlResultsDto,
    ]);

    // Extract tag categories from content-filtered results
    const extractedTagCategories = useMemo(() => {
        return extractTagCategoriesFromControlResults(
            existingControlResults,
            futureControlResults
        );
    }, [existingControlResults, futureControlResults]);

    const tagFilteredControlResults = useMemo(() => {
        return applyTagFiltering(existingControlResults, filteredTagNames);
    }, [existingControlResults, filteredTagNames]);

    const tagsFutureFilteredControlResults = useMemo(() => {
        return applyTagFiltering(futureControlResults, filteredTagNames);
    }, [futureControlResults, filteredTagNames]);

    const filteredControlResultsContent = useMemo(() => {
        return applyEntityFiltering(
            tagFilteredControlResults,
            filteredEntityIds
        );
    }, [tagFilteredControlResults, filteredEntityIds]);

    const filteredFutureControlResultsContent = useMemo(() => {
        return applyEntityFiltering(
            tagsFutureFilteredControlResults,
            filteredEntityIds
        );
    }, [tagsFutureFilteredControlResults, filteredEntityIds]);

    const {
        filteredControlPrograms,
        filteredControlResults,
        filteredFutureControlResults,
    } = useMemo(() => {
        if (!controlProgramsDto || !controlResultsDto) {
            return {
                filteredControlPrograms: [],
                filteredControlResults: [],
                filteredFutureControlResults: [],
            };
        }

        const filteredDto = {
            ...controlResultsDto,
            content: filteredControlResultsContent,
        };

        if (!futureControlResultsDto) {
            return getFilteredProgramsAndResults(
                showInactivePrograms,
                controlProgramsDto,
                filteredDto,
                defaultPaginatedListDto
            );
        }

        const futureFilteredDto = {
            ...futureControlResultsDto,
            content: filteredFutureControlResultsContent,
        };

        return getFilteredProgramsAndResults(
            showInactivePrograms,
            controlProgramsDto,
            filteredDto,
            futureFilteredDto
        );
    }, [
        controlProgramsDto,
        controlResultsDto,
        filteredControlResultsContent,
        futureControlResultsDto,
        filteredFutureControlResultsContent,
        showInactivePrograms,
    ]);

    const filteredControlResultsDto: ControlResultPaginatedListDto =
        useMemo<ControlResultPaginatedListDto>(() => {
            return controlResultsDto
                ? {
                      pagination: controlResultsDto.pagination,
                      content: filteredControlResults,
                      linkedResources: {
                          controlResultUserPools:
                              controlResultsDto.linkedResources
                                  .controlResultUserPools,
                          controls: controlResultsDto.linkedResources.controls,
                          users: controlResultsDto.linkedResources.users,
                          controlPrograms: filteredControlPrograms,
                          entities: controlResultsDto.linkedResources.entities,
                          classification: null,
                      },
                  }
                : defaultPaginatedListDto;
        }, [
            controlResultsDto,
            filteredControlResults,
            filteredControlPrograms,
        ]);

    const filteredFutureControlResultsDto: FutureControlResultPaginatedListDto =
        useMemo<FutureControlResultPaginatedListDto>(() => {
            return futureControlResultsDto
                ? {
                      pagination: futureControlResultsDto.pagination,
                      content: filteredFutureControlResults,
                      linkedResources: {
                          controlResultUserPools:
                              futureControlResultsDto.linkedResources
                                  .controlResultUserPools,
                          controls:
                              futureControlResultsDto.linkedResources.controls,
                          users: futureControlResultsDto.linkedResources.users,
                          controlPrograms: filteredControlPrograms,
                          entities:
                              futureControlResultsDto.linkedResources.entities,
                          classification: null,
                      },
                  }
                : defaultPaginatedListDto;
        }, [
            futureControlResultsDto,
            filteredFutureControlResults,
            filteredControlPrograms,
        ]);

    return {
        filteredControlPrograms,
        filteredControlResultsDto,
        filteredFutureControlResultsDto,
        dateRange,
        isLoading: !isComplete,
        extractedTagCategories,
    };
}

function getFilteredProgramsAndResults(
    showInactivePrograms: boolean,
    controlProgramsDto: ControlProgramOverviewResources,
    controlResultsDto: ControlResultPaginatedListDto,
    futureControlResultsDto: FutureControlResultPaginatedListDto
) {
    const controlPrograms = controlProgramsDto.content;
    const controlResultPrograms =
        controlResultsDto.linkedResources.controlPrograms;
    const futureControlResultPrograms =
        futureControlResultsDto.linkedResources.controlPrograms;

    const filteredControlPrograms = controlResultPrograms.filter((crp) =>
        controlPrograms.some(
            (cp) => cp.resource.controlProgram.id === crp.resource.id
        )
    );
    const filteredFutureControlPrograms = futureControlResultPrograms.filter(
        (crp) =>
            controlPrograms.some(
                (cp) => cp.resource.controlProgram.id === crp.resource.id
            )
    );

    const mergerdControlPrograms = [
        ...filteredControlPrograms,
        ...filteredFutureControlPrograms,
    ].filter(deduplicateArrayByKeyCallback((cp) => cp.resource.id));

    const contentFilteredFutureProgramIds = new Set(
        filteredFutureControlPrograms.map((cp) => cp.resource.id)
    );

    const allExistingProgramIds = new Set(
        controlPrograms.map((cp) => cp.resource.controlProgram.id)
    );

    // Absent means deleted, which counts as inactive — the same rule
    // the activities list applies, so a drill-through matches these counts.
    // Note this active/inactive filtering does not affect the future activities.
    const runningByProgramId = new Map(
        controlResultPrograms.map((cp) => [cp.resource.id, cp.resource.running])
    );

    const filteredResults = controlResultsDto.content.filter(({ resource }) => {
        const running = runningByProgramId.get(resource.controlProgramId);
        return running === true || showInactivePrograms;
    });

    const filteredFutureResults = futureControlResultsDto.content.filter(
        (crDto) => {
            const controlProgramId = crDto.resource.controlProgramId;

            if (!allExistingProgramIds.has(controlProgramId)) {
                return true;
            }

            return contentFilteredFutureProgramIds.has(controlProgramId);
        }
    );

    const programIdsWithExistingResults = new Set(
        filteredResults.map((cr) => cr.resource.controlProgramId)
    );

    const programIdsWithFutureResults = new Set(
        filteredFutureResults.map((cr) => cr.resource.controlProgramId)
    );

    // Only include programs that have results in either filtered set
    const finalMergedFilteredPrograms = mergerdControlPrograms.filter(
        (cp) =>
            programIdsWithExistingResults.has(cp.resource.id) ||
            programIdsWithFutureResults.has(cp.resource.id)
    );
    return {
        filteredControlPrograms: finalMergedFilteredPrograms,
        filteredControlResults: filteredResults,
        filteredFutureControlResults: filteredFutureResults,
    };
}

function isPaginatedQueryComplete(
    enabled: boolean,
    {
        data,
        isError,
        isPlaceholderData,
        hasNextPage,
        isFetchingNextPage,
    }: UseInfiniteQueryResult
) {
    if (!enabled || isError) {
        return true;
    }
    return (
        data !== undefined &&
        !isPlaceholderData &&
        !hasNextPage &&
        !isFetchingNextPage
    );
}
